import { useEffect, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Bookmark, ChevronLeft, ChevronRight, ClipboardList, Copy, Download, ExternalLink, History, Plus, Search, X } from "lucide-react";
import { http } from "../../api/http";
import { Badge, Button, Callout, Card, CardHeader, EmptyState, SelectField, cx, type BadgeTone } from "../../components/charte";
import { useAuthStore } from "../../store/auth.store";

// ─── Dossiers à traiter : vues filtrées et actions groupées ──────────────────
// Logique inchangée (filtres dans l'adresse, prévisualisation → exécution → reprise,
// identifiant du lot en sessionStorage, CSV protégé) ; seule la présentation change.

const filters: Record<string, string> = {
  all: "Tous les apprenants",
  incomplete: "Dossiers incomplets",
  expiring_document: "Pièces expirées ou expirant sous 30 jours",
  no_next_lesson: "Sans prochaine leçon",
  code_expiring: "Code expirant sous 30 jours",
  unpaid: "Factures échues non réglées",
};
/** Libellés courts des filtres (pastilles) ; le libellé complet reste en info-bulle. */
const shortFilters: Record<string, string> = {
  all: "Tous",
  incomplete: "Dossiers incomplets",
  expiring_document: "Pièces à renouveler",
  no_next_lesson: "Sans prochaine leçon",
  code_expiring: "Code expirant",
  unpaid: "Factures impayées",
};
type Row = {
  id: string;
  firstName: string;
  lastName: string;
  neph: string;
  version: number;
};
type BulkResult = {
  total: number;
  processed: number;
  complete: boolean;
  action: string;
  results: {
    id: string;
    label?: string;
    state: string;
    taskId?: string;
    recipient?: string;
    body?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  }[];
};

const RESULT_STATE: Record<string, { label: string; tone: BadgeTone }> = {
  done: { label: "Effectué", tone: "ok" },
  prepared: { label: "Relance préparée, non envoyée", tone: "info" },
  assignee_unavailable: { label: "Responsable devenu indisponible : refaire une sélection", tone: "warn" },
  conflict: { label: "Dossier modifié : refaire une sélection après vérification", tone: "warn" },
};

export default function CandidateWorklist() {
  const scope = useAuthStore(
    (s) => `${s.user?.id}:${s.user?.role}:${s.activeSchoolId}`,
  );
  const role = useAuthStore((s) => s.user?.role);
  const [params, setParams] = useSearchParams();
  const filter = params.get("filter") || "all";
  const search = params.get("search") || "";
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [selected, setSelected] = useState<string[]>([]);
  const [all, setAll] = useState(false);
  const [operation, setOperation] = useState("assign");
  const [assignee, setAssignee] = useState("");
  const [preview, setPreview] = useState<{
    id: string;
    total: number;
    items: Row[];
  } | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);
  const [error, setError] = useState("");
  // Vues enregistrées : nom saisi dans la page (plus de fenêtre du navigateur),
  // suppression après confirmation.
  const [naming, setNaming] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const savedViews = useQuery({
    queryKey: ["office-views", scope],
    queryFn: async () =>
      (
        await http.get<
          { id: string; label: string; filter: string; search: string }[]
        >("/v1/candidate-office/views")
      ).data,
    enabled: ["owner", "manager", "agent"].includes(role ?? ""),
  });
  const saved = savedViews.data ?? [];
  const [resumeId, setResumeId] = useState<string | null>(null);
  useEffect(() => {
    try {
      setResumeId(sessionStorage.getItem("office-run:" + scope));
    } catch {
      setResumeId(null);
    }
  }, [scope]);
  const allowed = ["owner", "manager", "agent"].includes(role ?? "");
  const data = useQuery({
    queryKey: ["office-list", scope, filter, search, page],
    queryFn: async ({ signal }) =>
      (
        await http.get<{ items: Row[]; total: number; hasMore: boolean }>(
          "/v1/candidate-office/list",
          { params: { filter, search, page }, signal },
        )
      ).data,
    enabled: allowed,
  });
  const members = useQuery({
    queryKey: ["office-members", scope],
    queryFn: async () =>
      (
        await http.get<{ id: string; firstName: string; lastName: string }[]>(
          "/v1/candidate-office/members",
        )
      ).data,
    enabled: allowed,
  });
  useEffect(() => {
    setSelected([]);
    setAll(false);
    setPreview(null);
    setResult(null);
  }, [filter, search, scope]);
  const action = useMutation({
    mutationFn: (fn: () => Promise<void>) => fn(),
    onError: (e: any) =>
      setError(
        e.response?.data?.message ||
          "Opération interrompue. La reprise conserve les résultats déjà enregistrés.",
      ),
  });
  const change = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    if (key !== "page") next.set("page", "1");
    setParams(next, { replace: true });
  };
  const execute = () =>
    action.mutate(async () => {
      const r = await http.post<BulkResult>(
        `/v1/candidate-office/bulk/${preview!.id}/execute`,
        { confirmed: true },
      );
      setResult(r.data);
      setError("");
      void data.refetch();
    });
  const exportFile = () => {
    if (!result) return;
    const cell = (v?: string) =>
      '"' +
      String(v ?? "")
        .replace(/^[\s]*[=+@\-\t\r]/, "'$&")
        .replace(/"/g, '""') +
      '"';
    const rows = result.results.filter((r) => r.state === "done");
    const csv =
      "Prénom;Nom;E-mail\r\n" +
      rows
        .map((r) => [r.firstName, r.lastName, r.email].map(cell).join(";"))
        .join("\r\n");
    const url = URL.createObjectURL(
      new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = "selection-eleves.csv";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const saveView = () => {
    const label = naming?.trim();
    if (!label) return;
    action.mutate(async () => {
      await http.post("/v1/candidate-office/views", {
        label: label.slice(0, 60),
        filter,
        search,
      });
      await savedViews.refetch();
      setNaming(null);
    });
  };
  // Le responsable ne sert qu'à « Affecter » et « Créer des tâches ».
  const needsAssignee = operation === "assign" || operation === "tasks";
  const selectionCount = all ? data.data?.total ?? 0 : selected.length;

  const page_ = (children: ReactNode) => (
    <div className="charte-root min-h-full bg-surface-light font-sans text-body antialiased tracking-[-.2px]">
      <header className="bg-hero-indigo pb-6 pt-[22px]">
        <div className="mx-auto w-full max-w-[1240px] px-10 max-md:px-5 max-sm:px-4">
          <nav aria-label="Fil d’Ariane" className="mb-4 flex items-center gap-1.5 text-[13px] text-muted">
            <Link to="/candidates" className="hover:text-primary">Apprenants</Link>
            <ChevronRight size={14} aria-hidden />
            <span className="font-medium text-body">Dossiers à traiter</span>
          </nav>
          <h1 className="text-title-lg text-heading max-sm:text-[28px] max-sm:tracking-[-1.2px]">Dossiers à traiter</h1>
          <p className="mt-2 max-w-3xl text-sm text-secondary">
            Filtrez les dossiers puis préparez vos actions. Aucun envoi ni opération financière automatique.
          </p>
        </div>
      </header>
      <div className="mx-auto w-full max-w-[1240px] px-10 pb-24 pt-5 max-md:px-5 max-sm:px-4">{children}</div>
    </div>
  );

  if (!allowed)
    return page_(
      <Card>
        <p role="alert" className="text-sm text-secondary">
          Ce suivi administratif est réservé au personnel habilité.
        </p>
      </Card>,
    );

  return page_(
    <div className="grid grid-cols-[minmax(0,1fr)_380px] items-start gap-6 max-lg:grid-cols-1">
      {/* ── Liste des élèves ── */}
      <Card className="!p-0 overflow-hidden">
        <div className="flex flex-col gap-4 border-b border-surface-2 p-5">
          {/* Vues (filtres) */}
          <div role="radiogroup" aria-label="Vue" className="flex flex-wrap gap-1.5">
            {Object.keys(filters).map((key) => {
              const on = key === filter;
              return (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  title={filters[key]}
                  onClick={() => change("filter", key)}
                  className={cx(
                    "h-8 rounded-pill border px-3.5 text-[13px] font-medium transition-colors",
                    on ? "border-heading bg-heading text-white" : "border-line bg-white text-secondary hover:border-primary-200 hover:text-heading",
                  )}
                >
                  {shortFilters[key]}
                </button>
              );
            })}
          </div>

          <label className="relative">
            <span className="sr-only">Rechercher</span>
            <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" aria-hidden />
            <input
              type="search"
              value={search}
              maxLength={120}
              onChange={(e) => change("search", e.target.value)}
              placeholder="Rechercher : nom, téléphone, e-mail, NEPH"
              className="h-10 w-full rounded-xl border border-line bg-white pl-9 pr-4 text-sm text-heading outline-none placeholder:text-muted focus:border-primary-300 focus:shadow-[0_0_0_4px_#EEF2FF]"
            />
          </label>

          {filter === "unpaid" && (
            <p className="text-xs text-muted">
              Factures émises qui restent à régler après leur échéance, d’après les encaissements et avoirs de l’apprenant.
            </p>
          )}
          {filter === "code_expiring" && (
            <p className="text-xs text-muted">
              Source : date de validité du code vérifiée et renseignée dans le suivi administratif.
            </p>
          )}

          {/* Vues enregistrées */}
          <div className="flex flex-wrap items-center gap-1.5">
            {saved.map((v) =>
              confirmDelete === v.id ? (
                <span key={v.id} className="inline-flex h-8 items-center gap-1 rounded-pill border border-danger/30 bg-danger-soft pl-3 pr-1 text-[13px] text-danger-dark">
                  Supprimer « {v.label} » ?
                  <button
                    type="button"
                    className="rounded-pill px-2 font-semibold hover:bg-white/60"
                    onClick={() =>
                      action.mutate(async () => {
                        await http.delete("/v1/candidate-office/views/" + v.id);
                        await savedViews.refetch();
                        setConfirmDelete(null);
                      })
                    }
                  >
                    Oui
                  </button>
                  <button type="button" className="rounded-pill px-2 hover:bg-white/60" onClick={() => setConfirmDelete(null)}>
                    Non
                  </button>
                </span>
              ) : (
                <span key={v.id} className="inline-flex h-8 items-center rounded-pill border border-primary-200 bg-primary-50 text-[13px] font-medium text-primary-600">
                  <button
                    type="button"
                    className="flex h-full items-center gap-1.5 pl-3 pr-1.5"
                    onClick={() => setParams({ filter: v.filter, search: v.search, page: "1" })}
                  >
                    <Bookmark size={13} aria-hidden />
                    {v.label}
                  </button>
                  <button
                    type="button"
                    aria-label={"Supprimer la vue " + v.label}
                    className="mr-1 grid h-6 w-6 place-items-center rounded-full hover:bg-white"
                    onClick={() => setConfirmDelete(v.id)}
                  >
                    <X size={13} aria-hidden />
                  </button>
                </span>
              ),
            )}
            {naming === null ? (
              <Button size="sm" variant="ghost" icon={Plus} disabled={action.isPending} onClick={() => setNaming(filters[filter])}>
                Enregistrer cette vue
              </Button>
            ) : (
              <form
                className="inline-flex items-center gap-1.5"
                onSubmit={(e) => {
                  e.preventDefault();
                  saveView();
                }}
              >
                <input
                  autoFocus
                  aria-label="Nom de la vue"
                  maxLength={60}
                  value={naming}
                  onChange={(e) => setNaming(e.target.value)}
                  onKeyDown={(e) => e.key === "Escape" && setNaming(null)}
                  className="h-8 w-[220px] rounded-pill border border-primary-300 px-3 text-[13px] outline-none shadow-[0_0_0_3px_#EEF2FF]"
                />
                <Button size="sm" variant="soft" type="submit" loading={action.isPending}>
                  Enregistrer
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setNaming(null)}>
                  Annuler
                </Button>
              </form>
            )}
            {resumeId && !preview && (
              <Button
                size="sm"
                variant="ghost"
                icon={History}
                className="ml-auto"
                onClick={() =>
                  action.mutate(async () => {
                    const r = await http.get<BulkResult>(
                      "/v1/candidate-office/bulk/" + resumeId,
                    );
                    setPreview({ id: resumeId, total: r.data.total, items: [] });
                    setResult(r.data);
                  })
                }
              >
                Retrouver mon dernier lot
              </Button>
            )}
          </div>
        </div>

        {data.isError ? (
          <div className="p-5">
            <EmptyState
              icon={ClipboardList}
              title="Liste indisponible."
              description="Vérifiez la connexion puis réessayez."
              action={<Button size="sm" onClick={() => void data.refetch()}>Réessayer</Button>}
            />
          </div>
        ) : data.isPending ? (
          <p className="px-5 py-6 text-sm text-muted">Chargement…</p>
        ) : (
          <>
            <p className="m-0 flex min-h-[44px] flex-wrap items-center gap-x-2 px-5 py-2.5 text-xs text-secondary">
              <span>
                <strong className="font-semibold text-heading">{data.data?.total ?? 0}</strong> apprenant(s) correspondent au filtre
              </span>
              {selectionCount > 0 && (
                <>
                  <span aria-hidden className="text-muted">·</span>
                  <span className="font-medium text-primary-600">
                    {all
                      ? `${data.data?.total ?? 0} sélectionné(s), sur toutes les pages`
                      : `${selected.length} sélectionné(s)`}
                  </span>
                </>
              )}
            </p>
            {data.data?.items.length ? (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-y border-line text-left text-[11px] font-bold uppercase tracking-[.8px] text-muted">
                    <th className="w-12 px-5 py-2.5 font-bold">
                      {/* Case d'en-tête : sélectionne tous les résultats du filtre, pages suivantes comprises. */}
                      <input
                        type="checkbox"
                        aria-label={`Sélectionner tous les résultats, sur toutes les pages (${data.data.total})`}
                        title="Sélectionner tous les résultats, sur toutes les pages"
                        className="h-4 w-4 align-middle accent-[#6366F1]"
                        checked={all}
                        ref={(el) => {
                          if (el) el.indeterminate = !all && selected.length > 0;
                        }}
                        onChange={(e) => {
                          setAll(e.target.checked);
                          setSelected([]);
                          setPreview(null);
                        }}
                      />
                    </th>
                    <th className="py-2.5 pr-3 font-bold">Apprenant</th>
                    <th className="py-2.5 pr-5 font-bold">NEPH</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.items.map((row) => {
                    const checked = all || selected.includes(row.id);
                    return (
                      <tr key={row.id} className={cx("border-b border-surface-2 last:border-b-0", checked && "bg-primary-50/50")}>
                        <td className="px-5 py-3">
                          <input
                            aria-label={`Sélectionner ${row.firstName} ${row.lastName}`}
                            type="checkbox"
                            className="h-4 w-4 accent-[#6366F1]"
                            checked={checked}
                            disabled={all}
                            onChange={(e) => {
                              setSelected((v) =>
                                e.target.checked
                                  ? [...v, row.id]
                                  : v.filter((id) => id !== row.id),
                              );
                              setPreview(null);
                            }}
                          />
                        </td>
                        <td className="py-3 pr-3">
                          <Link to={`/candidates/${row.id}`} className="font-medium text-heading hover:text-primary-600">
                            {row.firstName} {row.lastName}
                          </Link>
                        </td>
                        <td className="py-3 pr-5 tabular-nums text-secondary">{row.neph}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="px-5 pb-5">
                <EmptyState icon={ClipboardList} title="Aucun dossier pour cette vue" description="Choisissez une autre vue ou modifiez la recherche." />
              </div>
            )}
            <nav aria-label="Pagination" className="flex items-center justify-center gap-3 border-t border-surface-2 px-5 py-3">
              <Button size="sm" icon={ChevronLeft} disabled={page === 1} onClick={() => change("page", String(page - 1))}>
                Précédent
              </Button>
              <span className="text-[13px] text-secondary">Page {page}</span>
              <Button size="sm" iconEnd={ChevronRight} disabled={!data.data?.hasMore} onClick={() => change("page", String(page + 1))}>
                Suivant
              </Button>
            </nav>
          </>
        )}
      </Card>

      {/* ── Action groupée ── */}
      <Card className="lg:sticky lg:top-4">
        <CardHeader eyebrow="Préparer une action groupée" />
        <h2 className="sr-only">Préparer une action groupée</h2>
        <p className="mb-4 text-[13px] text-secondary">
          {all
            ? `${data.data?.total ?? 0} résultats sur toutes les pages`
            : `${selected.length} apprenant(s) sélectionné(s), y compris sur les autres pages`}
        </p>
        <div className="flex flex-col gap-3">
          <SelectField
            label="Action"
            value={operation}
            onChange={(e) => {
              setOperation(e.target.value);
              if (e.target.value !== "assign" && e.target.value !== "tasks") setAssignee("");
              setPreview(null);
            }}
          >
            <option value="assign">Affecter un responsable administratif</option>
            <option value="tasks">Créer des tâches de suivi</option>
            <option value="reminders">Préparer des relances</option>
            <option value="export">Exporter la sélection</option>
          </SelectField>
          {needsAssignee && (
            <SelectField
              label={operation === "tasks" ? "Confier les tâches à" : "Responsable"}
              value={assignee}
              onChange={(e) => {
                setAssignee(e.target.value);
                setPreview(null);
              }}
            >
              <option value="">Non affecté</option>
              {members.data?.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.firstName} {m.lastName}
                </option>
              ))}
            </SelectField>
          )}
          <Button
            variant="primary"
            disabled={action.isPending || (!all && !selected.length)}
            onClick={() =>
              action.mutate(async () => {
                const r = await http.post("/v1/candidate-office/bulk/preview", {
                  filter,
                  search,
                  action: operation,
                  assigneeId: assignee || undefined,
                  allMatching: all,
                  candidateIds: selected,
                });
                setPreview(r.data);
                setResumeId(r.data.id);
                try {
                  sessionStorage.setItem("office-run:" + scope, r.data.id);
                } catch {
                  /* Le lot reste conservé côté serveur. */
                }
                setResult(null);
                setError("");
              })
            }
          >
            Prévisualiser
          </Button>
          {!selectionCount && <p className="text-xs text-muted">Cochez des apprenants dans la liste pour préparer une action.</p>}
        </div>

        {error && (
          <p role="status" className="mt-4 rounded-row bg-warn-soft px-3.5 py-2.5 text-[13px] text-warn-fg">
            {error}
          </p>
        )}

        {preview && (
          <div className="mt-5 border-t border-surface-2 pt-4">
            <h3 className="text-sm font-semibold text-heading">Confirmer pour {preview.total} apprenant(s)</h3>
            <p className="mt-1 text-xs text-muted">
              La sélection est figée à la prévisualisation. Une modification concurrente sera signalée par apprenant.
            </p>
            {preview.items.length > 0 && (
              <ul className="mt-3 max-h-[180px] overflow-auto rounded-row bg-surface-light px-3.5 py-2 text-[13px] text-heading">
                {preview.items.map((r) => (
                  <li key={r.id} className="py-0.5">
                    {r.firstName} {r.lastName}
                  </li>
                ))}
              </ul>
            )}
            {preview.total > 25 && (
              <p className="mt-2 text-xs text-muted">25 noms affichés sur {preview.total} sélectionnés.</p>
            )}
            <Button
              variant="soft"
              className="mt-3 w-full"
              disabled={action.isPending || result?.complete}
              onClick={execute}
            >
              {result && !result.complete
                ? "Reprendre les apprenants restants"
                : "Confirmer et appliquer"}
            </Button>
          </div>
        )}

        {result && (
          <div className="mt-5 border-t border-surface-2 pt-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-heading">
              {result.processed} / {result.total} traités ·{" "}
              {result.complete ? "Lot terminé" : "Lot à poursuivre"}
            </p>
            {result.action === "export" && result.complete && (
              <Button size="sm" icon={Download} className="mt-3" onClick={exportFile}>
                Télécharger le CSV
              </Button>
            )}
            <ul className="mt-3 flex flex-col gap-2">
              {result.results.map((r) => {
                const state = RESULT_STATE[r.state] ?? { label: "Inaccessible", tone: "mute" as BadgeTone };
                return (
                  <li key={r.id} className="rounded-row border border-line px-3.5 py-2.5 text-[13px]">
                    <div className="flex flex-wrap items-center gap-2">
                      <strong className="font-semibold text-heading">{r.label || "Apprenant inaccessible"}</strong>
                      <Badge tone={state.tone}>{state.label}</Badge>
                      {r.taskId && (
                        <Link to={`/workspace/tasks?taskId=${r.taskId}`} className="ml-auto inline-flex items-center gap-1 text-primary-600 hover:underline">
                          Ouvrir la tâche <ExternalLink size={12} aria-hidden />
                        </Link>
                      )}
                    </div>
                    {r.body && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-secondary">
                          Brouillon pour {r.recipient || "adresse à compléter"}
                        </summary>
                        <p className="mt-2 whitespace-pre-line rounded-row bg-surface-light px-3 py-2 text-heading">{r.body}</p>
                        <Button
                          size="sm"
                          icon={Copy}
                          className="mt-2"
                          onClick={() =>
                            void navigator.clipboard
                              .writeText(r.body!)
                              .catch(() => setError("Copie impossible."))
                          }
                        >
                          Copier le brouillon
                        </Button>
                      </details>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <Callout className="mt-5" tone="note">
          Aucune action n’est envoyée à l’apprenant automatiquement : les relances restent des brouillons à copier.
        </Callout>
      </Card>
    </div>,
  );
}
